import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Select, Space, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
// Api
import { GET_BRAND_RESTRICTED_CATEGORIES } from 'api/brandRestrictedCategory/queries';
import { DELETE_BRAND_RESTRICTED_CATEGORY } from 'api/brandRestrictedCategory/mutations';
// Types
import {
  DeleteBrandRestrictedCategory,
  DeleteBrandRestrictedCategoryVariables,
} from 'api/brandRestrictedCategory/types/DeleteBrandRestrictedCategory';
import { GetBrandRestrictedCategories_getBrandRestrictedCategories } from 'api/brandRestrictedCategory/types/GetBrandRestrictedCategories';
// UI
import { errorNotification, successNotification } from 'ui/Notification';

type BrandRestrictedCategory =
  GetBrandRestrictedCategories_getBrandRestrictedCategories;

export type DeleteBrandRestrictedCategoryModalProps = {
  record: BrandRestrictedCategory | null;
  categories: BrandRestrictedCategory[];
  onClose: () => void;
};

const { Text, Paragraph } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const DeleteBrandRestrictedCategoryModal = ({
  record,
  categories,
  onClose,
}: DeleteBrandRestrictedCategoryModalProps): JSX.Element => {
  const [transferToCategoryId, setTransferToCategoryId] = useState<
    string | undefined
  >();

  const recordId = record?.id;

  useEffect(() => {
    setTransferToCategoryId(undefined);
  }, [recordId]);

  const [deleteBrandRestrictedCategory, { loading }] = useMutation<
    DeleteBrandRestrictedCategory,
    DeleteBrandRestrictedCategoryVariables
  >(DELETE_BRAND_RESTRICTED_CATEGORY, {
    refetchQueries: [{ query: GET_BRAND_RESTRICTED_CATEGORIES }],
  });

  const transferOptions = useMemo(
    () => categories.filter((category) => category.id !== recordId),
    [categories, recordId]
  );

  const usedByBrandsCount = record?.usedByBrandsCount ?? 0;
  const usedByTalentsCount = record?.usedByTalentsCount ?? 0;
  const hasUsage = usedByBrandsCount > 0 || usedByTalentsCount > 0;
  const canTransfer = transferOptions.length > 0;

  const handleDelete = async (targetId?: string | null) => {
    if (!record) {
      return;
    }

    try {
      await deleteBrandRestrictedCategory({
        variables: {
          input: {
            id: record.id,
            transferToCategoryId: targetId || null,
          },
        },
      });
      successNotification('Restricted category removed');
      onClose();
    } catch (error) {
      errorNotification(
        (error as Error)?.message ||
          'Looks like something went wrong. Please try again later.'
      );
    }
  };

  const handleDeleteAndTransfer = async () => {
    if (!transferToCategoryId) {
      return;
    }

    await handleDelete(transferToCategoryId);
  };

  const handleDeleteWithoutTransfer = () => {
    confirm({
      title: `Delete "${record?.name}" without transferring?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <Paragraph>
          All brand, team, and talent selections linked to this category will be
          removed. This cannot be undone.
        </Paragraph>
      ),
      okText: 'Delete without transferring',
      okButtonProps: { danger: true },
      onOk: () => handleDelete(null),
    });
  };

  const handleCancel = () => {
    setTransferToCategoryId(undefined);
    onClose();
  };

  return (
    <Modal
      title={record ? `Delete "${record.name}"?` : 'Delete restricted category'}
      visible={!!record}
      onCancel={handleCancel}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          {hasUsage && (
            <Button
              danger
              loading={loading}
              onClick={handleDeleteWithoutTransfer}
            >
              Delete without transferring
            </Button>
          )}
          {(!hasUsage || canTransfer) && (
            <Button
              type="primary"
              danger
              loading={loading}
              disabled={hasUsage && !transferToCategoryId}
              onClick={() => {
                if (hasUsage) {
                  handleDeleteAndTransfer();
                  return;
                }

                handleDelete(null);
              }}
            >
              {hasUsage ? 'Delete and transfer' : 'Delete'}
            </Button>
          )}
        </Space>
      }
    >
      {hasUsage ? (
        <>
          <Paragraph>
            This category is used by <Text strong>{usedByBrandsCount}</Text>{' '}
            brand{usedByBrandsCount === 1 ? '' : 's'} and{' '}
            <Text strong>{usedByTalentsCount}</Text> talent
            {usedByTalentsCount === 1 ? '' : 's'}.
          </Paragraph>

          {canTransfer ? (
            <>
              <Paragraph>
                Choose another category to move those selections to. A transfer
                target is required by default.
              </Paragraph>
              <Select
                showSearch
                placeholder="Move selections to…"
                style={{ width: '100%' }}
                value={transferToCategoryId}
                onChange={setTransferToCategoryId}
                optionFilterProp="children"
                allowClear
              >
                {transferOptions.map((category) => (
                  <Option key={category.id} value={category.id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </>
          ) : (
            <Paragraph type="secondary">
              There are no other restricted categories available to transfer to.
              You can only delete without transferring.
            </Paragraph>
          )}
        </>
      ) : (
        <Paragraph>
          Are you sure you want to delete this restricted category?
        </Paragraph>
      )}
    </Modal>
  );
};

export default DeleteBrandRestrictedCategoryModal;
